import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaCheck, FaSpinner, FaChevronLeft, FaChevronRight, FaKeyboard, FaPencilAlt, FaUndo } from 'react-icons/fa';
import { Skeleton } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const DocumentSigningModal = ({ isOpen, onClose, documentUrl, signatureBoxes = [], onSubmit, isSubmitting }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [signatures, setSignatures] = useState({}); // { boxIndex: { type: 'typed'|'drawn', data: string } }
  const [activeBoxIndex, setActiveBoxIndex] = useState(null);
  const [signMode, setSignMode] = useState('type'); // 'type' or 'draw'
  const [typedText, setTypedText] = useState('');
  const [selectedFont, setSelectedFont] = useState("'Dancing Script', cursive");

  const SIGNATURE_FONTS = [
    { name: 'Dancing Script', css: "'Dancing Script', cursive" },
    { name: 'Great Vibes', css: "'Great Vibes', cursive" },
    { name: 'Pacifico', css: "'Pacifico', cursive" },
    { name: 'Sacramento', css: "'Sacramento', cursive" },
    { name: 'Satisfy', css: "'Satisfy', cursive" },
    { name: 'Caveat', css: "'Caveat', cursive" },
    { name: 'Kalam', css: "'Kalam', cursive" },
    { name: 'Permanent Marker', css: "'Permanent Marker', cursive" },
    { name: 'Homemade Apple', css: "'Homemade Apple', cursive" },
    { name: 'Rock Salt', css: "'Rock Salt', cursive" },
    { name: 'Indie Flower', css: "'Indie Flower', cursive" },
    { name: 'Shadows Into Light', css: "'Shadows Into Light', cursive" },
  ];
  const [loading, setLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);

  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  // Load PDF
  useEffect(() => {
    if (!isOpen || !documentUrl) return;
    setLoading(true);
    const loadPdf = async () => {
      try {
        const doc = await pdfjsLib.getDocument(documentUrl).promise;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setLoading(false);
      }
    };
    loadPdf();
    return () => {
      setPdfDoc(null);
      setSignatures({});
      setActiveBoxIndex(null);
    };
  }, [isOpen, documentUrl]);

  // Render page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    try {
      const page = await pdfDoc.getPage(currentPage);
      const container = containerRef.current;
      const containerWidth = container ? container.clientWidth - 48 : 800;
      const viewport = page.getViewport({ scale: 1 });
      
      // Calculate display scale to fit container
      const displayScale = containerWidth / viewport.width;
      setScale(displayScale);
      
      // Use higher render scale for quality (2x for retina, min 2.0 for crisp rendering)
      const devicePixelRatio = window.devicePixelRatio || 1;
      const renderScale = displayScale * Math.max(devicePixelRatio, 2.0);
      
      const renderViewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      canvas.width = renderViewport.width;
      canvas.height = renderViewport.height;
      
      // Set CSS size to display scale for proper sizing
      canvas.style.width = `${viewport.width * displayScale}px`;
      canvas.style.height = `${viewport.height * displayScale}px`;

      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  }, [pdfDoc, currentPage, activeBoxIndex]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Lock body scroll
  useEffect(() => {
    if (!isOpen || !modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [isOpen, modalRoot]);

  // Drawing on signature pad
  const startDrawing = (e) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearDrawCanvas = () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Apply signature to box
  const applySignature = () => {
    if (activeBoxIndex === null) return;
    if (signMode === 'type') {
      if (!typedText.trim()) return;
      setSignatures(prev => ({
        ...prev,
        [activeBoxIndex]: { type: 'typed', data: typedText.trim(), font: selectedFont }
      }));
    } else {
      const canvas = drawCanvasRef.current;
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      // Check if canvas is empty
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const isEmpty = !imageData.data.some((ch, i) => i % 4 === 3 && ch !== 0);
      if (isEmpty) return;
      setSignatures(prev => ({
        ...prev,
        [activeBoxIndex]: { type: 'drawn', data: dataUrl }
      }));
    }
    setActiveBoxIndex(null);
    setTypedText('');
  };

  // Check all boxes are signed
  const allSigned = signatureBoxes.length > 0 && signatureBoxes.every((_, idx) => signatures[idx]);

  // Handle submit: render signatures onto the PDF canvas and create a blob
  const handleSubmit = async () => {
    if (!allSigned || !pdfDoc) return;

    try {
      // Create a combined canvas for each page with signatures
      // Use high quality scale (3.0) to maintain original PDF quality
      const HIGH_QUALITY_SCALE = 3.0;
      
      const { jsPDF } = await import('jspdf');
      const firstPage = await pdfDoc.getPage(1);
      const firstViewport = firstPage.getViewport({ scale: HIGH_QUALITY_SCALE });
      const pdf = new jsPDF({
        orientation: firstViewport.width > firstViewport.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [firstViewport.width, firstViewport.height],
        compress: false // Disable compression to maintain quality
      });

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) {
          const pg = await pdfDoc.getPage(pageNum);
          const vp = pg.getViewport({ scale: HIGH_QUALITY_SCALE });
          pdf.addPage([vp.width, vp.height], vp.width > vp.height ? 'landscape' : 'portrait');
        }

        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: HIGH_QUALITY_SCALE });

        // Render PDF page to offscreen canvas at high quality
        const offCanvas = document.createElement('canvas');
        offCanvas.width = viewport.width;
        offCanvas.height = viewport.height;
        const ctx = offCanvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;

        // Draw signatures on this page
        const pageBoxes = signatureBoxes
          .map((box, idx) => ({ ...box, idx }))
          .filter(b => b.page === pageNum - 1);

        for (const box of pageBoxes) {
          const sig = signatures[box.idx];
          if (!sig) continue;
          const bx = box.x * HIGH_QUALITY_SCALE;
          const by = box.y * HIGH_QUALITY_SCALE;
          const bw = box.width * HIGH_QUALITY_SCALE;
          const bh = box.height * HIGH_QUALITY_SCALE;

          if (sig.type === 'typed') {
            ctx.save();
            const fontFamily = sig.font || "'Dancing Script', cursive";
            // Scale font size proportionally for high quality rendering
            const fontSize = Math.min(bh * 0.6, 96); // Increased max from 32 to 96 for 3x scale
            ctx.font = `${fontSize}px ${fontFamily}`;
            ctx.fillStyle = '#1a1a2e';
            ctx.textBaseline = 'middle';
            ctx.fillText(sig.data, bx + 6, by + bh / 2, bw - 12);
            ctx.restore();
          } else if (sig.type === 'drawn') {
            const img = new Image();
            await new Promise((resolve) => {
              img.onload = resolve;
              img.src = sig.data;
            });
            ctx.drawImage(img, bx, by, bw, bh);
          }
        }

        // Add page image to PDF
        const pageDataUrl = offCanvas.toDataURL('image/png');
        pdf.addImage(pageDataUrl, 'PNG', 0, 0, viewport.width, viewport.height);
      }

      const pdfBlob = pdf.output('blob');
      onSubmit(pdfBlob);
    } catch (err) {
      console.error('Error creating signed PDF:', err);
      toast.error('Failed to create signed document. Please try again.');
    }
  };

  const currentPageBoxes = signatureBoxes
    .map((box, idx) => ({ ...box, idx }))
    .filter(b => b.page === currentPage - 1);

  if (!isOpen || !modalRoot) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-[95%] max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-700 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-white">Sign Document</h2>
            <p className="text-indigo-200 text-sm mt-0.5">Click on each signature box to sign</p>
          </div>
          <button onClick={onClose} disabled={isSubmitting} className="p-2 rounded-lg text-white hover:bg-white/20 transition disabled:opacity-50">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* PDF View - Only shown when NOT signing a specific box */}
        {activeBoxIndex === null && (
          <>
            {/* Toolbar */}
            <div className="px-4 py-2.5 md:px-6 md:py-3 bg-gray-50 dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="p-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition">
                  <FaChevronLeft />
                </button>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Page {currentPage} / {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="p-2 bg-white dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition">
                  <FaChevronRight />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {Object.keys(signatures).length} / {signatureBoxes.length} signed
                </span>
                <button
                  onClick={handleSubmit}
                  disabled={!allSigned || isSubmitting}
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg hover:shadow-lg disabled:opacity-50 transition"
                >
                  {isSubmitting ? <><FaSpinner className="animate-spin" /> Submitting...</> : <><FaCheck /> Submit</>}
                </button>
              </div>
            </div>
    
            {/* Main Content - PDF Preview */}
            <div ref={containerRef} className="flex-1 min-h-0 overflow-auto p-4 md:p-6 bg-gray-100 dark:bg-dark-900 flex justify-center">
              {loading ? (
                <div className="flex items-center justify-center h-96 w-full max-w-2xl">
                  <Skeleton className="h-full w-full max-h-[500px] rounded-lg" />
                  <p className="sr-only">Loading document...</p>
                </div>
              ) : (
                <div className="relative inline-block shadow-xl rounded-lg bg-white">
                  <canvas ref={canvasRef} className="block" style={{ display: 'block' }} />
    
                  {/* Signature box overlays */}
                  {currentPageBoxes.map((box) => {
                    const signed = signatures[box.idx];
                    return (
                      <div
                        key={box.idx}
                        onClick={() => { if (!signed) { setActiveBoxIndex(box.idx); setSignMode('type'); setTypedText(''); } }}
                        className={`absolute flex items-center justify-center cursor-pointer transition-all ${
                          signed
                            ? 'border-2 border-green-500 bg-green-50 bg-opacity-30'
                            : 'border-2 border-dashed border-red-500 bg-red-50 bg-opacity-20 hover:bg-opacity-40 hover:border-red-600'
                        }`}
                        style={{
                          left: box.x * scale,
                          top: box.y * scale,
                          width: box.width * scale,
                          height: box.height * scale,
                        }}
                      >
                        {signed ? (
                          signed.type === 'typed' ? (
                            <span
                              className="text-gray-900 px-1 truncate w-full text-center"
                              style={{ fontFamily: signed.font || "'Dancing Script', cursive", fontSize: `${Math.min(box.height * scale * 0.55, 28)}px` }}
                            >
                              {signed.data}
                            </span>
                          ) : (
                            <img src={signed.data} alt="signature" className="w-full h-full object-contain p-0.5" />
                          )
                        ) : (
                          <span className="text-xs text-red-500 font-medium animate-pulse">Click to Sign</span>
                        )}
                        {signed && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setSignatures(prev => { const n = { ...prev }; delete n[box.idx]; return n; }); }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow hover:bg-red-600"
                            title="Remove signature"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Signature View - Only shown when a specific box IS being signed */}
        {activeBoxIndex !== null && (
          <div className="flex-1 min-h-0 bg-white dark:bg-dark-800 p-4 md:p-5 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                  Sign Box {activeBoxIndex + 1}
                </h3>
                <button onClick={() => { setActiveBoxIndex(null); setTypedText(''); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <FaTimes />
                </button>
              </div>

              {/* Mode Tabs */}
              <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-dark-700 p-1 rounded-xl">
                <button
                  onClick={() => setSignMode('type')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    signMode === 'type' ? 'bg-white dark:bg-dark-600 text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaKeyboard /> Type Signature
                </button>
                <button
                  onClick={() => setSignMode('draw')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    signMode === 'draw' ? 'bg-white dark:bg-dark-600 text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FaPencilAlt /> Draw Signature
                </button>
              </div>

              {/* Type Mode */}
              {signMode === 'type' && (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={typedText}
                    onChange={(e) => setTypedText(e.target.value)}
                    placeholder="Type your full name..."
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                    autoFocus
                  />
                  {typedText && (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 font-medium">Select a signature style:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                        {SIGNATURE_FONTS.map((font, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedFont(font.css)}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${
                              selectedFont === font.css
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 shadow-md'
                                : 'border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 hover:border-gray-400'
                            }`}
                          >
                            <p className="text-xs text-gray-400 mb-1 truncate">{font.name}</p>
                            <p
                              className="text-xl text-gray-900 dark:text-white truncate"
                              style={{ fontFamily: font.css }}
                            >
                              {typedText}
                            </p>
                          </button>
                        ))}
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-xl border border-gray-200 dark:border-dark-600">
                        <p className="text-xs text-gray-500 mb-2">Preview:</p>
                        <p className="text-3xl text-gray-900 dark:text-white" style={{ fontFamily: selectedFont }}>
                          {typedText}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Draw Mode */}
              {signMode === 'draw' && (
                <div className="space-y-3">
                  <div className="relative border-2 border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden bg-white">
                    <canvas
                      ref={drawCanvasRef}
                      width={600}
                      height={150}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      className="w-full cursor-crosshair"
                      style={{ touchAction: 'none' }}
                    />
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-gray-300 pointer-events-none">
                      Sign here
                    </div>
                  </div>
                  <button
                    onClick={clearDrawCanvas}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                  >
                    <FaUndo /> Clear
                  </button>
                </div>
              )}

              {/* Apply Button */}
              <button
                onClick={applySignature}
                className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition flex items-center justify-center gap-2"
              >
                <FaCheck /> Apply Signature
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    modalRoot
  );
};

export default DocumentSigningModal;
